import React, { useState } from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';

import { Alert, Box, Container } from '@mui/material';

import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import HeatmapSurcharge from '~/pages/LERE/HeatmapSurcharge';

import useCurrentUser from '~/hooks/useCurrentUser';

import { hasFullAccess, hasReadAccess } from '~/utils/lere';

import PageHeader from '../Common/Elements/PageHeader';

import HurdleHeatmaps from './HurdleHeatmaps';
import LereTPFMEmailTrigger from './LereTPFMEmailTrigger';
import LereUserActivity from './LereUserActivity';
import RMChangeLog from './RMChangeLog';
import RMExclusionOverrides from './RMExclusionOverrides';
import RMSurchargeSuggestions from './RMSurchargeSuggestions';
import RMToursPricingChangeLog from './RMToursPricingChangeLog';
import RMToursPricingSuggestions from './RMToursPricingSuggestions';
import SurchargeEstimates from './SurchargeEstimates';

export const TabsMap = {
  'surcharge-estimates': {
    label: 'Surcharge Estimates',
    slug: 'surcharge-estimates',
    component: SurchargeEstimates,
  },
  'rm-surcharge-suggestions': {
    label: '[RM] Surcharge Suggestions',
    slug: 'rm-surcharge-suggestions',
    component: RMSurchargeSuggestions,
  },
  'rm-change-log': {
    label: '[RM] Hotels Change Log',
    slug: 'rm-change-log',
    component: RMChangeLog,
  },
  'rm-exclusion-overrides': {
    label: '[RM] Exclusions',
    slug: 'rm-exclusion-overrides',
    component: RMExclusionOverrides,
  },
  'rm-tours-pricing-suggestions': {
    label: '[RM] Tours Pricing Suggestions',
    slug: 'rm-tours-pricing-suggestions',
    component: RMToursPricingSuggestions,
  },
  'rm-tours-pricing-history': {
    label: '[RM] Tours Change Log',
    slug: 'rm-tours-pricing-history',
    component: RMToursPricingChangeLog,
  },
  heatmaps: {
    label: 'Heatmaps',
    slug: 'heatmaps',
    component: HurdleHeatmaps,
  },
  'heatmap-surcharge': {
    label: 'Heatmap Surcharge',
    slug: 'heatmap-surcharge',
    component: HeatmapSurcharge,
  },
  'email-trigger': {
    label: 'Email Trigger',
    slug: 'email-trigger',
    component: LereTPFMEmailTrigger,
  },
  'user-activity': {
    label: 'User Activity',
    slug: 'user-activity',
    component: LereUserActivity,
  },
};
type TabKey = keyof typeof TabsMap;
const allTabs = Object.keys(TabsMap);

preRegisterSpotlightRoutes(expandTabRoutes('/lere', 'LERE', TabsMap));

export default function LereAdminPageContainer() {
  const { section = 'rm-surcharge-suggestions' } = useParams<{ section?: string }>();
  const { user } = useCurrentUser();
  const currentTab = TabsMap[section as TabKey];
  const title = currentTab ? `LERE / ${currentTab.label}` : 'LERE';
  const subtitle = section === 'surcharge-estimates' ? 'Dynamic Pricing AB Test Surcharge Estimation' : undefined;
  const [lereError, setLereError] = useState<Error | undefined>(undefined);

  if (!hasFullAccess(user) && !hasReadAccess(user)) {
    return (
      <Container sx={{ backgroundColor: 'background.default' }}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: 480, width: '100%' }}>
          You do not have the correct permissions to access this page.
        </Alert>
      </Container>
    );
  }

  if (!currentTab) {
    return <Navigate to="/lere" replace />;
  }

  return (
    <Container sx={{ backgroundColor: 'background.default' }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader title={title} subtitle={subtitle} />

      {lereError && (
        <Alert severity="error" onClose={() => setLereError(undefined)}>
          {lereError.message ?? lereError.toString()}
        </Alert>
      )}

      <SectionNavigationLayout
        navigation={
          <SectionNavigation
            label="LERE sections"
            currentTab={section}
            groups={[{ items: allTabs.map((id) => ({ id, label: TabsMap[id].label, href: `/lere/${id}` })) }]}
          />
        }
      >
        <Box mt={4}>
          <Box>
            <currentTab.component onLereError={setLereError} />
          </Box>
        </Box>
      </SectionNavigationLayout>
    </Container>
  );
}
